import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Platform } from 'react-native';

interface ToastContextType {
  showToast: (message: string, actionLabel?: string, onAction?: () => void) => void;
  hideToast: () => void;
  toastVisible: boolean;
  toastMessage: string;
  toastActionLabel?: string;
  toastOnAction?: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastActionLabel, setToastActionLabel] = useState<string | undefined>(undefined);
  const [toastOnAction, setToastOnAction] = useState<(() => void) | undefined>(undefined);

  const showToast = useCallback((message: string, actionLabel?: string, onAction?: () => void) => {
    // Only show toast on web platform
    if (Platform.OS !== 'web') {
      return;
    }
    
    setToastMessage(message);
    setToastActionLabel(actionLabel);
    setToastOnAction(() => onAction);
    setToastVisible(true);
  }, []);

  const hideToast = useCallback(() => {
    setToastVisible(false);
    // Clear values after animation completes
    setTimeout(() => {
      setToastMessage('');
      setToastActionLabel(undefined);
      setToastOnAction(undefined);
    }, 300);
  }, []);

  const value: ToastContextType = {
    showToast,
    hideToast,
    toastVisible,
    toastMessage,
    toastActionLabel,
    toastOnAction,
  };

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
};








